import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Presenter from './Presenter';

const API_BASE_URL = process.env.REACT_APP_PLATFORM_API_BASE_URL;

export interface PaymentReturnData {
    status: 'approved' | 'rejected' | 'pending' | string;
    bookingId: string;
    collectionId?: string;
    collectionStatus?: string;
    preferenceId?: string;
    paymentId?: string;
    paymentType?: string;
    merchantOrderId?: string;
    timestamp?: string;
}

const STORAGE_KEY = 'paymentResultData';

const PaymentResultContainer = () => {
    const history = useHistory();
    const location = useLocation();
    const [paymentData, setPaymentData] = useState<PaymentReturnData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadPaymentData();
    }, [location.pathname]);

    const loadPaymentData = async () => {
        // 1. Try URL params (direct from MercadoPago redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status') || urlParams.get('collection_status');
        const bookingId = urlParams.get('external_reference');

        if (status && bookingId) {
            const data: PaymentReturnData = {
                status,
                bookingId,
                collectionId: urlParams.get('collection_id') || undefined,
                collectionStatus: urlParams.get('collection_status') || undefined,
                preferenceId: urlParams.get('preference_id') || undefined,
                paymentId: urlParams.get('payment_id') || undefined,
                paymentType: urlParams.get('payment_type') || undefined,
                merchantOrderId: urlParams.get('merchant_order_id') || undefined,
                timestamp: new Date().toISOString()
            };
            setPaymentData(data);

            // Save to sessionStorage for page refresh recovery
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));

            // Clean URL to avoid leaking payment params
            window.history.replaceState({}, '', location.pathname);
            setIsLoading(false);
            return;
        }

        // 2. Fallback: Try sessionStorage (if URL was already cleaned after redirect)
        const storedData = sessionStorage.getItem(STORAGE_KEY);
        if (storedData) {
            try {
                const parsed = JSON.parse(storedData) as PaymentReturnData;
                setPaymentData(parsed);
                setIsLoading(false);
                return;
            } catch (_error) {
                // Corrupted data, continue to next fallback
            }
        }

        // 3. Last resort: Use pendingPaymentBookingId to fetch status from API
        const pendingBookingId = sessionStorage.getItem('pendingPaymentBookingId');
        if (pendingBookingId && API_BASE_URL) {
            try {
                const response = await axios.get(`${API_BASE_URL}/payment/${pendingBookingId}`);
                if (response.data?.success && response.data?.data) {
                    const apiData = response.data.data;
                    const statusMap: Record<string, string> = {
                        APPROVED: 'approved',
                        REJECTED: 'rejected',
                        PENDING: 'pending',
                        IN_PROCESS: 'pending',
                        CANCELLED: 'rejected',
                    };
                    const data: PaymentReturnData = {
                        status: statusMap[apiData.status] || 'pending',
                        bookingId: apiData.bookingId,
                        timestamp: new Date().toISOString()
                    };
                    setPaymentData(data);
                    setIsLoading(false);
                    return;
                }
            } catch (_error) {
                // API call failed, show no data state
            }
        }

        setIsLoading(false);
    };

    const handleGoHome = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem('pendingPaymentBookingId');
        history.push('/');
    };

    const handleRetry = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem('pendingPaymentBookingId');
        history.push('/');
    };

    return (
        <Presenter
            paymentData={paymentData}
            isLoading={isLoading}
            onGoHome={handleGoHome}
            onRetry={handleRetry}
        />
    );
};

export default PaymentResultContainer;
